// Markdown-редактор поверх Milkdown (ProseMirror + Remark).
// БД хранит чистый markdown, Milkdown парсит его в ProseMirror state и
// рендерит WYSIWYG (Live Preview), как в Obsidian.
//
// Custom plugins (lib/milkdown):
//   - wikilink: [[target]] / [[target|alias]]
//   - tag: #word → фиолетовый pill
//   - callout: :::info / :::warn / :::idea
//
// Frontmatter (YAML) хранится отдельным jsonb-полем в БД, для UI есть
// отдельная панель сверху редактора (теги/статус).
//
// Source/Preview toggle: Milkdown WYSIWYG ↔ CodeMirror 6.
// Оба биндятся к одной React-state markdown-строке.

import { useEffect, useMemo, useRef, useState } from 'react';
import { Editor, rootCtx, defaultValueCtx, editorViewOptionsCtx } from '@milkdown/core';
import { commonmark } from '@milkdown/preset-commonmark';
import { gfm } from '@milkdown/preset-gfm';
import { history } from '@milkdown/plugin-history';
import { listener, listenerCtx } from '@milkdown/plugin-listener';
import { clipboard } from '@milkdown/plugin-clipboard';
import { prism, prismConfig } from '@milkdown/plugin-prism';
import { getMarkdown } from '@milkdown/utils';
import bash from 'refractor/lang/bash.js';
import css from 'refractor/lang/css.js';
import javascript from 'refractor/lang/javascript.js';
import typescript from 'refractor/lang/typescript.js';
import json from 'refractor/lang/json.js';
import jsx from 'refractor/lang/jsx.js';
import tsx from 'refractor/lang/tsx.js';
import markdown from 'refractor/lang/markdown.js';
import python from 'refractor/lang/python.js';
import sql from 'refractor/lang/sql.js';
import yamlLang from 'refractor/lang/yaml.js';
import go from 'refractor/lang/go.js';
import rust from 'refractor/lang/rust.js';
import html from 'refractor/lang/markup.js';
import { Milkdown, MilkdownProvider, useEditor } from '@milkdown/react';
import { EditorView } from '@codemirror/view';
import { EditorState } from '@codemirror/state';
import { markdown as cmMarkdown } from '@codemirror/lang-markdown';
import { oneDark } from '@codemirror/theme-one-dark';
import {
  ArrowRight,
  Link2,
  Tag as TagIcon,
  X,
  ChevronDown,
  ChevronRight,
  Network,
  Eye,
  Code as CodeIcon,
} from 'lucide-react';
import { api } from '../lib/api';
import { DocumentGraph } from './DocumentGraph';
import { DocumentToc } from './DocumentToc';
import { DocumentToolbar } from './DocumentToolbar';
import { type Frontmatter } from '../lib/milkdown';

interface DocumentEditorProps {
  treeNodeId: string;
  title: string;
  onActivate: (id: string) => void;
}

export function DocumentEditor({ treeNodeId, title, onActivate }: DocumentEditorProps) {
  const docQuery = api.documents.resolveByTreeNode.useQuery({ treeNodeId });
  const docId = docQuery.data?.id;

  if (!docId) {
    return (
      <main className="flex-1 flex items-center justify-center text-ink-faint_l dark:text-ink-faint_d">
        Подготовка документа…
      </main>
    );
  }

  return (
    <MilkdownProvider>
      <DocumentEditorInner docId={docId} initialTitle={title} onActivate={onActivate} />
    </MilkdownProvider>
  );
}

function DocumentEditorInner({
  docId,
  initialTitle,
  onActivate,
}: {
  docId: string;
  initialTitle: string;
  onActivate: (id: string) => void;
}) {
  const fullQuery = api.documents.getFull.useQuery({ docId });
  const utils = api.useUtils();
  // CRITICAL: НЕ invalidate'им getFull — иначе refetch меняет initialMarkdown,
  // что в свою очередь триггерило бы пересоздание editor'а посреди набора.
  // Вместо этого мерджим updated document в кеш через setData.
  const updateMutation = api.documents.update.useMutation({
    onSuccess: (updated, vars) => {
      utils.documents.getFull.setData({ docId: vars.id }, (old) =>
        old && updated ? { ...old, document: updated } : old,
      );
      utils.documents.graph.invalidate();
      if (vars.title !== undefined) utils.treeNodes.list.invalidate();
    },
  });
  // createMissing создаёт новые документы — нужен полный refetch
  const createMissingMutation = api.documents.createMissingFromLinks.useMutation({
    onSuccess: () => {
      utils.documents.getFull.invalidate({ docId });
      utils.documents.graph.invalidate();
      utils.treeNodes.list.invalidate();
    },
  });
  const updateFrontmatterMutation = api.documents.updateFrontmatter.useMutation({
    onSuccess: (updated, vars) => {
      utils.documents.getFull.setData({ docId: vars.id }, (old) =>
        old && updated ? { ...old, document: updated } : old,
      );
    },
  });

  const [title, setTitle] = useState(initialTitle);
  const [tab, setTab] = useState<'toc' | 'links' | 'graph'>('toc');
  const [liveMarkdown, setLiveMarkdown] = useState<string>('');
  const [showFrontmatter, setShowFrontmatter] = useState(false);
  const [viewMode, setViewMode] = useState<'preview' | 'source'>('preview');

  // Markdown source — единый источник истины для обоих view-режимов.
  const markdownRef = useRef<string>('');
  const [sourceText, setSourceText] = useState<string>('');
  const loadedDocIdRef = useRef<string | null>(null);
  const liveMarkdownTimerRef = useRef<number | null>(null);

  useEffect(() => {
    if (fullQuery.data) setTitle(fullQuery.data.document.title);
  }, [fullQuery.data]);

  const initialMarkdown = useMemo(() => {
    if (!fullQuery.data) return '';
    return fullQuery.data.document.content ?? '';
  }, [fullQuery.data]);

  useEffect(() => {
    if (!fullQuery.data) return;
    if (loadedDocIdRef.current === docId) return;
    loadedDocIdRef.current = docId;
    markdownRef.current = initialMarkdown;
    setSourceText(initialMarkdown);
    setLiveMarkdown(initialMarkdown);
  }, [docId, fullQuery.data, initialMarkdown]);

  // Cleanup при unmount: убиваем pending save timer + liveMarkdown timer
  useEffect(() => {
    return () => {
      cancelDebouncedSave(docId);
      if (liveMarkdownTimerRef.current != null) {
        window.clearTimeout(liveMarkdownTimerRef.current);
        liveMarkdownTimerRef.current = null;
      }
    };
  }, [docId]);

  const scheduleSave = (md: string) => {
    markdownRef.current = md;
    // Throttle обновление TOC — не пересчитываем заголовки на каждый keystroke,
    // только раз в 600ms (минимизирует re-render родителя на больших документах)
    if (liveMarkdownTimerRef.current != null) {
      window.clearTimeout(liveMarkdownTimerRef.current);
    }
    liveMarkdownTimerRef.current = window.setTimeout(() => {
      setLiveMarkdown(md);
      liveMarkdownTimerRef.current = null;
    }, 600);
    debouncedSave(docId, () => updateMutation.mutate({ id: docId, content: md }));
  };

  // Memoized — иначе regex-сканирование запускается на каждый родительский render
  // (а это каждые ~600ms throttled во время набора). Хук должен идти ДО early return
  // (правила React hooks: одинаковый порядок вызовов на каждом рендере).
  const forwardLinksLen = fullQuery.data?.forwardLinks.length ?? 0;
  const unresolvedCount = useMemo(
    () => countUnresolvedWikilinks(liveMarkdown || initialMarkdown, forwardLinksLen),
    [liveMarkdown, initialMarkdown, forwardLinksLen],
  );

  if (!fullQuery.data) {
    return (
      <main className="flex-1 flex items-center justify-center text-ink-faint_l dark:text-ink-faint_d">
        Загрузка документа…
      </main>
    );
  }

  const { document: doc, backlinks, forwardLinks } = fullQuery.data;

  const fm = (doc.frontmatter ?? {}) as Frontmatter;
  const tags: string[] = Array.isArray(fm.tags) ? (fm.tags as string[]) : [];
  const status: string = typeof fm.status === 'string' ? fm.status : '';

  const updateFrontmatter = (patch: Partial<{ tags: string[]; status: string }>) => {
    const newFm = { ...fm, ...patch };
    updateFrontmatterMutation.mutate({ id: docId, frontmatter: newFm });
  };

  return (
    <main className="flex-1 grid grid-cols-[1fr_280px] overflow-hidden">
      <div className="overflow-auto p-8" data-doc-scroll>
        <header className="mb-3">
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            onBlur={() => {
              if (title !== fullQuery.data?.document.title && title.trim()) {
                updateMutation.mutate({ id: docId, title: title.trim() });
              }
            }}
            className="w-full text-2xl font-bold bg-transparent focus:outline-none"
            placeholder="Заголовок"
          />
        </header>

        <div className="flex items-center gap-1 mb-4 text-xs">
          <button
            type="button"
            onClick={() => {
              if (viewMode === 'source') markdownRef.current = sourceText;
              setViewMode('preview');
            }}
            className={`inline-flex items-center gap-1 px-2.5 py-1 rounded transition ${
              viewMode === 'preview'
                ? 'bg-surface2-light dark:bg-surface2-dark text-ink-light dark:text-ink-dark'
                : 'text-ink-faint_l dark:text-ink-faint_d hover:text-ink-light dark:hover:text-ink-dark'
            }`}
          >
            <Eye size={12} />
            Live preview
          </button>
          <button
            type="button"
            onClick={() => {
              setSourceText(markdownRef.current);
              setViewMode('source');
            }}
            className={`inline-flex items-center gap-1 px-2.5 py-1 rounded transition ${
              viewMode === 'source'
                ? 'bg-surface2-light dark:bg-surface2-dark text-ink-light dark:text-ink-dark'
                : 'text-ink-faint_l dark:text-ink-faint_d hover:text-ink-light dark:hover:text-ink-dark'
            }`}
          >
            <CodeIcon size={12} />
            Источник
          </button>
        </div>

        <FrontmatterPanel
          show={showFrontmatter}
          onToggle={() => setShowFrontmatter((v) => !v)}
          tags={tags}
          status={status}
          onTagsChange={(t) => updateFrontmatter({ tags: t })}
          onStatusChange={(s) => updateFrontmatter({ status: s })}
        />

        {unresolvedCount > 0 && (
          <div className="mb-3 flex items-center gap-2 px-3 py-2 rounded bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 text-sm">
            <span className="text-amber-700 dark:text-amber-300">
              {unresolvedCount} {unresolvedCount === 1 ? 'wikilink ведёт' : 'wikilinks ведут'} на несуществующие документы.
            </span>
            <button
              type="button"
              onClick={() => createMissingMutation.mutate({ docId })}
              disabled={createMissingMutation.isPending}
              className="ml-auto text-xs px-2 py-1 rounded bg-amber-600 text-white hover:bg-amber-700 disabled:opacity-50"
            >
              {createMissingMutation.isPending ? 'Создаём…' : 'Создать недостающие'}
            </button>
          </div>
        )}

        {viewMode === 'preview' ? (
          <>
            <DocumentToolbar />
            <MilkdownView
              key={docId}
              initialMarkdown={initialMarkdown}
              onChange={scheduleSave}
              onClickWikilink={(target) => {
                void resolveAndActivate(target, onActivate, utils);
              }}
            />
          </>
        ) : (
          <SourceView
            key={docId}
            value={sourceText}
            onChange={(md) => {
              setSourceText(md);
              scheduleSave(md);
            }}
          />
        )}
      </div>

      <aside className="border-l border-line-light dark:border-line-dark bg-surface2-light dark:bg-surface2-dark overflow-auto text-sm flex flex-col">
        <div className="flex border-b border-line-light dark:border-line-dark sticky top-0 bg-surface2-light dark:bg-surface2-dark z-10">
          <button
            type="button"
            onClick={() => setTab('toc')}
            className={`flex-1 px-2 py-2 text-[11px] font-semibold uppercase tracking-wide transition ${
              tab === 'toc'
                ? 'text-accent border-b-2 border-accent'
                : 'text-ink-faint_l dark:text-ink-faint_d hover:text-ink-muted_l dark:hover:text-ink-muted_d'
            }`}
          >
            Оглавление
          </button>
          <button
            type="button"
            onClick={() => setTab('links')}
            className={`flex-1 px-2 py-2 text-[11px] font-semibold uppercase tracking-wide transition ${
              tab === 'links'
                ? 'text-accent border-b-2 border-accent'
                : 'text-ink-faint_l dark:text-ink-faint_d hover:text-ink-muted_l dark:hover:text-ink-muted_d'
            }`}
          >
            Ссылки
          </button>
          <button
            type="button"
            onClick={() => setTab('graph')}
            className={`flex-1 px-2 py-2 text-[11px] font-semibold uppercase tracking-wide flex items-center justify-center gap-1 transition ${
              tab === 'graph'
                ? 'text-accent border-b-2 border-accent'
                : 'text-ink-faint_l dark:text-ink-faint_d hover:text-ink-muted_l dark:hover:text-ink-muted_d'
            }`}
          >
            <Network size={11} />
            Граф
          </button>
        </div>

        <div className="p-4 space-y-5 flex-1">
          {tab === 'toc' && (
            <DocumentToc
              key={docId}
              markdown={liveMarkdown || initialMarkdown}
              containerSelector="[data-doc-scroll]"
            />
          )}
          {tab === 'links' && (
            <>
              <section>
                <h3 className="text-[10px] font-semibold uppercase tracking-wider text-ink-faint_l dark:text-ink-faint_d mb-2 flex items-center gap-1.5">
                  <ArrowRight size={11} />
                  Ссылается на
                  <span className="text-ink-faint_l dark:text-ink-faint_d normal-case tracking-normal font-normal">
                    ({forwardLinks.length})
                  </span>
                </h3>
                {forwardLinks.length === 0 ? (
                  <p className="text-xs text-ink-faint_l dark:text-ink-faint_d italic">Нет исходящих ссылок</p>
                ) : (
                  <ul className="space-y-1">
                    {forwardLinks.map((d) => (
                      <li key={d.id}>
                        <button
                          type="button"
                          onClick={() => onActivateByDocId(d.id, onActivate, utils)}
                          className="text-accent hover:underline text-[13px] text-left"
                        >
                          {d.title}
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </section>

              <section>
                <h3 className="text-[10px] font-semibold uppercase tracking-wider text-ink-faint_l dark:text-ink-faint_d mb-2 flex items-center gap-1.5">
                  <Link2 size={11} />
                  Бэклинки
                  <span className="text-ink-faint_l dark:text-ink-faint_d normal-case tracking-normal font-normal">
                    ({backlinks.length})
                  </span>
                </h3>
                {backlinks.length === 0 ? (
                  <p className="text-xs text-ink-faint_l dark:text-ink-faint_d italic">
                    Никто не ссылается на этот документ
                  </p>
                ) : (
                  <ul className="space-y-2">
                    {backlinks.map((d) => (
                      <li key={d.id} className="border-l-2 border-line-light dark:border-line-dark pl-2 hover:border-accent transition">
                        <button
                          type="button"
                          onClick={() => onActivateByDocId(d.id, onActivate, utils)}
                          className="text-accent hover:underline text-[13px] font-medium text-left"
                        >
                          {d.title}
                        </button>
                        {d.excerpt && (
                          <div className="text-[11px] text-ink-faint_l dark:text-ink-faint_d mt-0.5 line-clamp-2">
                            {d.excerpt}
                          </div>
                        )}
                      </li>
                    ))}
                  </ul>
                )}
              </section>

              <section className="pt-3 border-t border-line-light dark:border-line-dark flex items-center justify-between">
                <span className="text-[10px] uppercase tracking-wider text-ink-faint_l dark:text-ink-faint_d">Слов</span>
                <span className="text-[13px] tabular-nums font-medium">{fullQuery.data.document.wordCount}</span>
              </section>
            </>
          )}
          {tab === 'graph' && (
            <DocumentGraph
              focusDocId={docId}
              onOpenDoc={(targetDocId) => onActivateByDocId(targetDocId, onActivate, utils)}
            />
          )}
        </div>
      </aside>
    </main>
  );
}

// === MilkdownView: WYSIWYG md-редактор ===

function MilkdownView({
  initialMarkdown,
  onChange,
  onClickWikilink,
}: {
  initialMarkdown: string;
  onChange: (md: string) => void;
  onClickWikilink: (target: string) => void;
}) {
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  // initialMarkdown захватываем в ref ОДИН раз при mount — иначе каждый refetch
  // (после save) меняет initialMarkdown, useEditor пересоздаёт editor, текст
  // мигает / пропадает / скролл сбивается. Смена документа = remount через
  // key={docId} в родителе.
  const initialMarkdownRef = useRef(initialMarkdown);

  useEditor(
    (root) =>
      Editor.make()
        .config((ctx) => {
          ctx.set(rootCtx, root);
          ctx.set(defaultValueCtx, initialMarkdownRef.current);
          // Отключаем браузерный spellchecker — иначе все технические термины
          // (HYPERPC, n8n, amoCRM, hyperpc_telephony) подчёркиваются красным.
          // Браузерный chrome/firefox spellcheck не понимает русский технический
          // словарь и сам по себе крайне неточен. Если нужно — пользователь
          // включает в системе или DevTools.
          ctx.update(editorViewOptionsCtx, (prev) => ({
            ...prev,
            attributes: {
              ...(typeof prev.attributes === 'function' ? {} : prev.attributes ?? {}),
              spellcheck: 'false',
              autocorrect: 'off',
              autocapitalize: 'off',
            },
          }));
          ctx.set(prismConfig.key, {
            configureRefractor: (r) => {
              r.register(bash);
              r.register(css);
              r.register(javascript);
              r.register(typescript);
              r.register(json);
              r.register(jsx);
              r.register(tsx);
              r.register(markdown);
              r.register(python);
              r.register(sql);
              r.register(yamlLang);
              r.register(go);
              r.register(rust);
              r.register(html);
            },
          });
          // Используем `updated` (cheap event с doc-state) вместо `markdownUpdated`,
          // который сериализует весь документ на каждый keystroke. Сериализация
          // вызывается только после 250ms тишины (debounced).
          let serializeTimer: number | null = null;
          ctx.get(listenerCtx).updated((listenerCtx) => {
            if (serializeTimer != null) window.clearTimeout(serializeTimer);
            serializeTimer = window.setTimeout(() => {
              const md = getMarkdown()(listenerCtx);
              onChangeRef.current(md);
              serializeTimer = null;
            }, 250);
          });
        })
        .use(commonmark)
        .use(gfm)
        .use(history)
        .use(listener)
        .use(clipboard)
        .use(prism),
    // Пустые deps. Смена документа = remount через key={docId} в родителе.
    // initialMarkdown захвачен в initialMarkdownRef один раз при mount.
    // refetch после save не должен пересоздавать editor — иначе теряем cursor/scroll/едет печать.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  // Делегированный click-handler на root — wikilinks не имеют React-listener'ов.
  useEffect(() => {
    const handler = (e: Event) => {
      const target = e.target as HTMLElement;
      if (!target) return;
      const link = target.closest('a[data-wikilink="true"]');
      if (link instanceof HTMLElement) {
        e.preventDefault();
        const wTarget = link.getAttribute('data-target') ?? link.textContent ?? '';
        if (wTarget) onClickWikilink(wTarget);
      }
    };
    document.addEventListener('click', handler);
    return () => document.removeEventListener('click', handler);
  }, [onClickWikilink]);

  return (
    <div className="milkdown-host doc-prose">
      <Milkdown />
    </div>
  );
}

// === SourceView: CodeMirror 6 ===

function SourceView({
  value,
  onChange,
}: {
  value: string;
  onChange: (md: string) => void;
}) {
  const hostRef = useRef<HTMLDivElement | null>(null);
  const viewRef = useRef<EditorView | null>(null);
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  useEffect(() => {
    if (!hostRef.current) return;
    const startState = EditorState.create({
      doc: value,
      extensions: [
        cmMarkdown(),
        oneDark,
        EditorView.theme({
          '&': { fontSize: '13px', minHeight: '400px' },
          '.cm-scroller': { fontFamily: 'ui-monospace, SF Mono, Menlo, Consolas, monospace' },
          '.cm-content': { padding: '12px' },
        }),
        EditorView.updateListener.of((v) => {
          if (v.docChanged) {
            onChangeRef.current(v.state.doc.toString());
          }
        }),
        EditorView.lineWrapping,
      ],
    });
    const view = new EditorView({ state: startState, parent: hostRef.current });
    viewRef.current = view;
    return () => {
      view.destroy();
      viewRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const view = viewRef.current;
    if (!view) return;
    const current = view.state.doc.toString();
    if (current !== value) {
      view.dispatch({ changes: { from: 0, to: current.length, insert: value } });
    }
  }, [value]);

  return (
    <div
      ref={hostRef}
      className="cm-host rounded border border-line-light dark:border-line-dark overflow-hidden"
    />
  );
}

// === Frontmatter panel ===

function FrontmatterPanel({
  show,
  onToggle,
  tags,
  status,
  onTagsChange,
  onStatusChange,
}: {
  show: boolean;
  onToggle: () => void;
  tags: string[];
  status: string;
  onTagsChange: (tags: string[]) => void;
  onStatusChange: (status: string) => void;
}) {
  const [newTag, setNewTag] = useState('');
  return (
    <div className="mb-3 rounded border border-line-light dark:border-line-dark bg-surface2-light dark:bg-surface2-dark">
      <button
        type="button"
        onClick={onToggle}
        className="w-full flex items-center gap-2 px-3 py-1.5 text-xs font-semibold uppercase tracking-wide text-ink-muted_l dark:text-ink-muted_d hover:text-ink-light dark:hover:text-ink-dark"
      >
        {show ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
        Frontmatter
        <span className="ml-auto text-[10px] font-normal text-ink-faint_l dark:text-ink-faint_d">
          {tags.length} тег(ов) {status && `· ${status}`}
        </span>
      </button>
      {show && (
        <div className="px-3 pb-3 space-y-3">
          <div>
            <label className="block text-[11px] font-semibold uppercase text-ink-muted_l dark:text-ink-muted_d mb-1">
              Статус
            </label>
            <input
              type="text"
              value={status}
              onChange={(e) => onStatusChange(e.target.value)}
              placeholder="draft / review / done / archived"
              className="w-full px-2 py-1 text-sm rounded border border-line-light dark:border-line-dark bg-surface-light dark:bg-surface-dark"
            />
          </div>
          <div>
            <label className="block text-[11px] font-semibold uppercase text-ink-muted_l dark:text-ink-muted_d mb-1">
              Теги
            </label>
            <div className="flex flex-wrap gap-1 mb-1.5">
              {tags.map((t) => (
                <button
                  key={t}
                  type="button"
                  onClick={() => onTagsChange(tags.filter((x) => x !== t))}
                  className="inline-flex items-center gap-1 px-2 py-0.5 text-xs rounded-full bg-accent/10 text-accent hover:bg-red-500/20 hover:text-red-500"
                >
                  <TagIcon size={10} />
                  {t}
                  <X size={10} />
                </button>
              ))}
            </div>
            <input
              value={newTag}
              onChange={(e) => setNewTag(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && newTag.trim() && !tags.includes(newTag.trim())) {
                  onTagsChange([...tags, newTag.trim()]);
                  setNewTag('');
                }
              }}
              placeholder="Новый тег + Enter"
              className="w-full px-2 py-1 text-xs rounded border border-line-light dark:border-line-dark bg-surface-light dark:bg-surface-dark"
            />
          </div>
        </div>
      )}
    </div>
  );
}

// === Helpers ===

function countUnresolvedWikilinks(md: string, resolvedCount: number): number {
  const cleaned = md.replace(/```[\s\S]*?```/g, '').replace(/`[^`]*`/g, '');
  const re = /\[\[([^\]|\n]+?)(?:\|[^\]\n]+?)?\]\]/g;
  const targets = new Set<string>();
  let m: RegExpExecArray | null;
  while ((m = re.exec(cleaned)) !== null) {
    targets.add(m[1].trim().toLowerCase());
  }
  return Math.max(0, targets.size - resolvedCount);
}

// Debounced save с поддержкой flush при unmount (не теряем последний keystroke).
const saveTimers = new Map<string, { timer: ReturnType<typeof setTimeout>; fn: () => void }>();
function debouncedSave(docId: string, fn: () => void, delay = 800) {
  const existing = saveTimers.get(docId);
  if (existing) clearTimeout(existing.timer);
  const t = setTimeout(() => {
    fn();
    saveTimers.delete(docId);
  }, delay);
  saveTimers.set(docId, { timer: t, fn });
}
// При unmount: вместо отмены — flush'им последнюю сохранённую функцию,
// чтобы текст не потерялся при переключении документа в момент debounce.
function cancelDebouncedSave(docId: string) {
  const existing = saveTimers.get(docId);
  if (existing) {
    clearTimeout(existing.timer);
    saveTimers.delete(docId);
    try {
      existing.fn();
    } catch {
      /* ignore */
    }
  }
}

async function resolveAndActivate(
  targetTitle: string,
  onActivate: (id: string) => void,
  utils: ReturnType<typeof api.useUtils>,
) {
  try {
    const docId = await utils.client.documents.findByTitle.query({ title: targetTitle });
    if (docId) {
      const treeId = await utils.client.documents.findTreeNodeByDocId.query({ docId });
      if (treeId) onActivate(treeId);
    }
  } catch {
    /* ignore */
  }
}

async function onActivateByDocId(
  targetDocId: string,
  onActivate: (id: string) => void,
  utils: ReturnType<typeof api.useUtils>,
) {
  try {
    const treeId = await utils.client.documents.findTreeNodeByDocId.query({ docId: targetDocId });
    if (treeId) onActivate(treeId);
  } catch {
    /* ignore */
  }
}
